import queryClient from "api/queryClient";
import { useUpdateLabelMutation } from "api/TenantSlug/Label/hooks.ts";
import { LabelFormData, schema } from "components/Forms/LabelsForm/schema.ts";
import { SharedLabelFormContent } from "components/Forms/LabelsForm/SharedLabelFormContent.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { toast } from "react-toastify";
import { FormId } from "types/models.ts";

import GetLabelResponse = App.Http.Responses.Label.GetLabelResponse;

interface EditLabelFormProps {
    onSuccess: () => void;
    label: GetLabelResponse["label"];
}

export const EditLabelForm: FC<EditLabelFormProps> = ({ label, onSuccess }) => {
    const formId = FormId.EditLabel;
    const { t } = usePaldockTranslation("forms.edit-label");
    const { handleErrorsFromErrorResponse } = useGeneralForm<LabelFormData>();

    const { mutate } = useUpdateLabelMutation();

    const handleSubmit = (data: LabelFormData) => {
        mutate(
            { labelId: label.id, request: data },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["getLabel", label.id] });
                    queryClient.invalidateQueries({ queryKey: ["getLabels"] });
                    onSuccess();
                    toast.success(t("messages.update_success"));
                },
                onError: (error) => {
                    handleErrorsFromErrorResponse(error);
                    toast.error(t("messages.update_error"));
                },
            }
        );
    };

    return (
        <GeneralFormProvider
            key={label?.id || "create"}
            id={formId}
            schema={schema}
            defaultValues={label}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <SharedLabelFormContent />
        </GeneralFormProvider>
    );
};
